/**
 * What the doc pages show (slice 2d spec §10): who changed a doc and when,
 * what it's linked to, the Linked panel's rows, a search's highlight, the
 * doc card's first lines, the conflict's "their change", and the editor's
 * draft (`app/draft.ts`, the Memory page's). Pure, so it's tested on its own
 * (`doc-view.test.ts`).
 */
import { kindOf, sizeLabel } from "../files/kinds";
import { parseBlocks, type Broken } from "../../../worker/domain/blocks.js";
import { draftKit, type Draft } from "../../app/draft";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import { panelHref } from "../chats/panel-state";
import { agentPhrase, counter } from "../memory/memory-view";
import type { TaskStage } from "../tasks/api";
import type { Doc, DocItem, DocLink, DocLinkVia, DocWho, Marks } from "./api";

/** A doc's body (`DOC_MAX`, §6.3) and its title, in UTF-16 units as the Worker counts. */
const DOC_MAX = 200_000;
export const TITLE_MAX = 200;

const count = new Intl.NumberFormat("en-US");

// ── Who and when ────────────────────────────────────────────────────────────

/** "You" for the signed-in person, else their name or the agent's. */
export function whoName(who: Pick<DocWho, "kind" | "id" | "name">, me: Pick<Me, "user">): string {
  return who.kind === "person" && who.id === me.user.id ? "You" : who.name;
}

/**
 * Who, as a sentence starts with them: "The Chief of Staff", "You", "Marcus"
 * (the Memory page's phrasing for an agent).
 */
export function whoPhrase(who: DocWho, me: Pick<Me, "user" | "agents">): string {
  if (who.kind === "agent") {
    const agent = me.agents.find((a) => a.id === who.id);
    const phrase = agent ? agentPhrase(agent) : who.name;
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
  }
  return whoName(who, me);
}

/** Who, after "Changed by" (§10.3): "Chief of Staff", "you", "Marcus". */
export function byPhrase(who: DocWho, me: Pick<Me, "user">): string {
  const name = whoName(who, me);
  return name === "You" ? "you" : name;
}

// ── Links ───────────────────────────────────────────────────────────────────

/** The list's Linked to (§10.2): the first link, named by its kind, and how many more. */
export type LinkedTo =
  | { kind: "none"; label: "Not linked" }
  | { kind: "task"; label: string; stage: TaskStage | null; more: number }
  | { kind: "other"; label: string; more: number };

const KIND_WORD: Record<Exclude<DocLink["target"], "task">, string> = {
  thread: "Chat",
  routine: "Routine",
};

export function linkedTo(links: DocLink[]): LinkedTo {
  const [first] = links;
  if (!first) return { kind: "none", label: "Not linked" };
  const more = links.length - 1;
  if (first.target === "task") {
    return { kind: "task", label: first.title, stage: first.stage ?? null, more };
  }
  return { kind: "other", label: `${KIND_WORD[first.target]} · ${first.title}`, more };
}

const linkedAt = (l: DocLink) => Date.parse(l.linked_at) || 0;
const changedAt = (l: DocLink) => Date.parse(l.updated_at ?? l.linked_at) || 0;

/** The doc's main task (§10.3): its first task link, by when it was linked. */
export function mainTask(links: DocLink[]): DocLink | null {
  const tasks = links.filter((l) => l.target === "task");
  return tasks.sort((a, b) => linkedAt(a) - linkedAt(b))[0] ?? null;
}

/** Ask to change's thread (§10.5): the most recently linked one, else none (a new chat). */
export function newestThread(links: DocLink[]): DocLink | null {
  const threads = links.filter((l) => l.target === "thread");
  return threads.sort((a, b) => linkedAt(b) - linkedAt(a))[0] ?? null;
}

/** The Linked panel's sections: tasks, then chats newest first (§10.3). */
export function linkedSections(links: DocLink[]): { tasks: DocLink[]; chats: DocLink[] } {
  const by = (target: DocLink["target"]) =>
    links.filter((l) => l.target === target).sort((a, b) => changedAt(b) - changedAt(a));
  return { tasks: by("task"), chats: by("thread") };
}

/** Chats shown before "Show all <n> chats": five beside the doc, three on a phone. */
export const CHATS_SHOWN = { wide: 5, phone: 3 } as const;

/** How a chat came to the doc, from its link's `via`; nothing for a plain link. */
export function viaLabel(via: DocLinkVia): string | null {
  if (via === "made") return "made this doc";
  if (via === "edited") return "edited this doc";
  if (via === "attached") return "asked about this doc";
  return null;
}

// ── Search ──────────────────────────────────────────────────────────────────

export type Segment = { text: string; mark: boolean };

/** `text` cut at the search's marks, for the highlight (§10.2, note 33). */
export function segments(text: string, marks: Marks): Segment[] {
  const sorted = [...marks]
    .filter(([s, e]) => e > s && s >= 0 && e <= text.length)
    .sort((a, b) => a[0] - b[0]);
  const out: Segment[] = [];
  let at = 0;
  for (const [start, end] of sorted) {
    if (start < at) continue;
    if (start > at) out.push({ text: text.slice(at, start), mark: false });
    out.push({ text: text.slice(start, end), mark: true });
    at = end;
  }
  if (at < text.length) out.push({ text: text.slice(at), mark: false });
  return out;
}

/**
 * The search box when the URL's `q` changes to `q`: the box's own text when
 * it's what wrote it (typing, debounced), else the URL's.
 */
export function searchTextFor(text: string, q: string): string {
  return text.trim() === q ? text : q;
}

/** "2 docs, best match first" over a search's rows. */
export function resultsLine(n: number): string {
  return `${count.format(n)} ${n === 1 ? "doc" : "docs"}, best match first`;
}

/** A file doc's line (slice 5b spec §9.1): its kind and size, "PDF · 1.2 MB"; null for any other. */
export function fileLine(item: Pick<DocItem, "file">): string | null {
  if (!item.file) return null;
  return `${kindOf(item.file).label} · ${sizeLabel(item.file.size)}`;
}

/**
 * The line under a row's title: a search's passage, with an ellipsis where
 * it starts or ends inside the body, else the body's start (a file doc's
 * kind and size).
 */
export function rowSnippet(
  item: Pick<DocItem, "snippet" | "marks" | "title" | "file">,
  searching: boolean,
): { text: string; marks: Marks; passage: boolean } {
  const passage = searching && item.marks.snippet.length > 0;
  const file = fileLine(item);
  if (!passage && file) return { text: file, marks: [], passage };
  if (!passage) return { text: plain(item.snippet, item.title), marks: [], passage };
  return { text: item.snippet, marks: item.marks.snippet, passage };
}

// ── Cards and rows ──────────────────────────────────────────────────────────

/**
 * Markdown made into a line of prose for a card or a row (§8: about 160
 * characters, markdown stripped): headings, emphasis, links, tables' pipes
 * and list marks go; a first heading that repeats the title goes too.
 */
export function plain(markdown: string, title?: string, max = 160): string {
  const text = withoutTitleHeading(markdown, title)
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(^|\s)#{1,6}\s+/g, "$1")
    .replace(/\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?/g, " ")
    .replace(/\s*\|\s*/g, " · ")
    // A bullet's mark and a task list's box; numbers stay ("Ava is 7.").
    .replace(/(^|\s)[-*+]\s+(?:\[[ xX]\]\s+)?/g, "$1")
    .replace(/[*_`~>]+/g, "")
    .replace(/(?:\s*·\s*)+/g, " · ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^·\s*|\s*·$/g, "");
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

/**
 * The body without a leading heading that is the title (a card shows the
 * title already). Only a `#` heading whose whole text is the title, bare or
 * in balanced wrappers of emphasis, code or strike, nested too (`**_Plan_**`:
 * the marks before it, mirrored after it); an unmatched mark (`Plan~`) isn't
 * a wrapper (Devin on #275). The heading is a line of its own, or, in a
 * snippet whose whitespace the API collapsed, the title ending at a word's
 * end ("# Plan Planning…" loses "# Plan"; "# Planning…" stays).
 */
function withoutTitleHeading(markdown: string, title?: string): string {
  const start = markdown.trimStart();
  const hashes = /^#{1,6}[ \t]+/.exec(start);
  const name = title?.trim();
  const after = hashes && name ? afterWrappedTitle(start.slice(hashes[0].length), name) : null;
  if (after === null) return start;
  if (start.includes("\n")) {
    const lineEnd = /^[ \t#]*(?:\n|$)/.exec(after);
    return lineEnd ? after.slice(lineEnd[0].length) : start;
  }
  return /^(?:\s|$)/.test(after) ? after : start;
}

/**
 * What follows `name` at the start of `text`, bare or in mirrored wrappers;
 * null when it isn't there. Each split of the leading marks is tried, no
 * wrapper first, so a title's own marks (`*Plan`) aren't taken for one.
 */
function afterWrappedTitle(text: string, name: string): string | null {
  const marks = /^[*_~`]*/.exec(text)?.[0] ?? "";
  for (let k = 0; k <= marks.length; k++) {
    const after = afterTitleIn(text, name, marks.slice(0, k));
    if (after !== null) return after;
  }
  return null;
}

function afterTitleIn(text: string, name: string, open: string): string | null {
  // The marks are ASCII, so splitting by code unit is exact.
  const close = open.split("").reverse().join("");
  const end = open.length + name.length;
  const found = text.slice(open.length, end).toLowerCase() === name.toLowerCase();
  return found && text.slice(end, end + close.length) === close
    ? text.slice(end + close.length)
    : null;
}

/**
 * The card's footer (§8): who changed it last and when, then its task (or
 * Archived). Board 50 draws "Doc · <task>"; the spec's who and when take the
 * place of "Doc" (Codex on #275).
 */
export function cardFooter(doc: DocItem | undefined, me: Me): string {
  if (!doc) return "Doc";
  const changed = `${whoName(doc.updated_by, me)} · ${shortTime(doc.updated_at, me.workspace.timezone)}`;
  const task = mainTask(doc.links);
  const after = doc.archived_at ? "Archived" : task?.title;
  return after ? `${changed} · ${after}` : changed;
}

// ── The editor ──────────────────────────────────────────────────────────────

/** What a person edits: the title and the body. */
export type DocDraft = { title: string; body: string };

export type DocEditor = Draft<Doc, DocDraft>;

export const docEditor = draftKit<Doc, DocDraft>(
  (doc) => ({ title: doc.title, body: doc.body }),
  (a, b) => a.title === b.title && a.body === b.body,
);

/** "1,318 of 200,000", copper from 90% and brick past the limit (Memory's tones). */
export function docCounter(length: number) {
  const { tone } = counter(length, DOC_MAX);
  return { text: `${count.format(length)} of ${count.format(DOC_MAX)}`, tone };
}

/**
 * New doc's first Save sent `sent`; `latest` is the draft when the doc came
 * back. Typing that went on meanwhile is carried into the doc's editor, still
 * unsaved; nothing changed, nothing is carried (Codex on #275).
 */
export function carriedDraft(sent: DocDraft, latest: DocDraft): DocDraft | null {
  return latest.title.trim() === sent.title && latest.body === sent.body ? null : latest;
}

/** Why the draft can't be saved as it is, or null. */
export function draftProblem(draft: DocDraft): string | null {
  const title = draft.title.trim();
  if (!title) return "Give it a title to save.";
  if (title.length > TITLE_MAX) {
    return `The title is ${count.format(title.length - TITLE_MAX)} over its ${TITLE_MAX} characters.`;
  }
  const over = draft.body.length - DOC_MAX;
  if (over > 0) {
    return `${count.format(over)} ${over === 1 ? "character" : "characters"} over the ${count.format(DOC_MAX)} limit. Shorten it to save.`;
  }
  return null;
}

/** Save only with a change that fits, and not while a conflict waits for a choice. */
export function canSaveDoc(e: DocEditor): boolean {
  return changes(e) && !draftProblem(e.draft) && !e.conflict;
}

/**
 * Whether the draft changes anything once it's sent: a title that differs
 * only by spaces at its ends isn't a change (`saveOf` trims it).
 */
function changes(e: DocEditor): boolean {
  const save = saveOf(e);
  return save.title !== undefined || save.body !== undefined;
}

/** What a save sends: only what changed, from the version the draft sits on. */
export function saveOf(e: DocEditor): { title?: string; body?: string; version: number } {
  const title = e.draft.title.trim();
  return {
    ...(title !== e.base.title ? { title } : {}),
    ...(e.draft.body !== e.base.body ? { body: e.draft.body } : {}),
    version: e.base.version,
  };
}

/**
 * Keep mine (48c): the draft moves onto their version and is saved from it.
 * When their version already is the draft (the same change saved twice, or
 * one that differs only by a title's end spaces), there's nothing to send:
 * the conflict just closes (Devin and Codex on #275).
 */
export function keepMineStep(e: DocEditor): {
  editor: DocEditor;
  save: ReturnType<typeof saveOf> | null;
} {
  const editor = docEditor.keepMine(e);
  if (changes(editor)) return { editor, save: saveOf(editor) };
  // Nothing to send, a title's end spaces included: their version is the
  // draft, so the draft becomes it (Codex on #275).
  return { editor: docEditor.editorFor(editor.base), save: null };
}

// ── A draft's blocks (slice 2g spec §9) ─────────────────────────────────────

/**
 * Whether a person can open the editor on `body`: not while it holds a block
 * that draws (a section, rows, a chart…), until there's a better way to edit
 * one than its JSON (the founder, 2026-10-12); Ask to change does it. A doc
 * of prose, or one whose only block is broken, stays editable, so a person
 * can fix what they typed. The API still saves either.
 */
/** Whether a doc is edited here: not a file doc (slice 5b §4), and its body can be (slice 2g §9). */
export const canEditDoc = (doc: Pick<Doc, "format" | "body">) =>
  doc.format !== "file" && canEditBody(doc.body);

export function canEditBody(body: string): boolean {
  return parseBlocks(body).every((b) => b.type === "text");
}

/** A broken block's line under the editor: "Line 4: this facts block won't draw: …". */
export function problemLine(b: Broken): string {
  return `Line ${b.line}: this ${b.type} block won't draw: ${b.why}.`;
}

/** Where line `line` (from 1) of `text` starts, for the cursor. */
export function lineOffset(text: string, line: number): number {
  let at = 0;
  for (let n = 1; n < line; n += 1) {
    const next = text.indexOf("\n", at);
    if (next < 0) return text.length;
    at = next + 1;
  }
  return at;
}

// ── The conflict ────────────────────────────────────────────────────────────

export type DiffLine = { sign: "+" | "-"; text: string };

/**
 * "Their change" (48c): the lines the newer version has that the one the
 * person started from doesn't (added), then the reverse (removed), each in
 * its own order, blank lines left out. A line counts as many times as it
 * appears, so a repeated line added once shows once. It's a summary to
 * decide by, not a diff to apply: lines that only moved don't show. Cut at
 * `max` lines; `more` counts the rest.
 */
export function lineDiff(
  before: string,
  after: string,
  max = 8,
): { lines: DiffLine[]; more: number } {
  const all: DiffLine[] = [
    ...unmatched(after.split("\n"), before.split("\n")).map((text) => ({
      sign: "+" as const,
      text,
    })),
    ...unmatched(before.split("\n"), after.split("\n")).map((text) => ({
      sign: "-" as const,
      text,
    })),
  ].filter((l) => l.text.trim() !== "");
  return { lines: all.slice(0, max), more: Math.max(0, all.length - max) };
}

/** The lines of `lines` that `other` doesn't have, counting repeats. */
function unmatched(lines: string[], other: string[]): string[] {
  const left = new Map<string, number>();
  for (const line of other) left.set(line, (left.get(line) ?? 0) + 1);
  return lines.filter((line) => {
    const n = left.get(line) ?? 0;
    if (n > 0) left.set(line, n - 1);
    return n === 0;
  });
}

// ── Ask to change ───────────────────────────────────────────────────────────

/**
 * Ask to change (§10.5): the side panel on the doc's most recently linked
 * thread, else a new chat, with the doc attached (`?attach=`).
 */
export function askHref(search: string, doc: Pick<Doc, "id" | "links">): string {
  const thread = newestThread(doc.links);
  const href = panelHref(search, thread?.id ?? "open");
  const next = new URLSearchParams(href.slice(1));
  next.set("attach", doc.id);
  return `?${next.toString()}`;
}

/** A system line's doc, when it's "Updated <title>" (§8). */
export function lineDoc(message: { kind: string; data: Record<string, unknown> }): string | null {
  if (message.kind !== "system" || message.data.event !== "doc_updated") return null;
  return typeof message.data.doc_id === "string" ? message.data.doc_id : null;
}

/** Where New doc's Create and edit goes: the editor, holding the title (and the task) until Save. */
export function newDocHref(title: string, taskId?: string, areaId?: string): string {
  const q = new URLSearchParams({ title });
  if (taskId) q.set("task", taskId);
  if (areaId) q.set("area", areaId);
  return `/docs/new?${q.toString()}`;
}

/** "example.com" for an image's link; the address itself when it isn't a URL. */
export function hostOf(src: string): string {
  try {
    return new URL(src).host || src;
  } catch {
    return src;
  }
}
